import { authRoutesAkto, registerAuthGuard, useAuthentificationStore } from '@/features/auth/authentification'
import { contextAppRoutes, registerContextGuard, registerPermissionsGuard } from '@/features/auth/context-app'
import { impersoRoutes } from '@/features/auth/impersonnalisation'
import { DispositifSousDipositif, TypePublic, type Dispositif } from '@/features/domaine/types/opco'
import {
  DispositifDRLibelle,
  SousDispositifLibelle as SousDispositifDRLibelle,
  type DispositifDR,
  type SousDispositif as SousDispositifDR,
} from '@/features/domaine/types/opco/Demande/DR'
import {
  SousDispositif as SousDispositifTT,
  SousDispositifLibelle as SousDispositifTTLibelle,
} from '@/features/domaine/types/opco/Demande/OUTILFPE'
import {
  createRouter,
  createWebHistory,
  type LocationQueryRaw,
  type RouteLocationNormalized,
  type RouteParamsGeneric,
  type RouteRecordRaw,
} from 'vue-router'

/* Injecte les services d'affichage des demandes */
import demande from '@/features/interface/demande/DemandeView.vue'
import { useDemandeRoutingService } from '@/features/interface/demande/shared/composables/useDemandeRoutingService'

/* Injecte les services de routage de recherche */
import DossierOutilFpeDetailView from '@/features/interface/detail/dossier-tt/DossierDetailView.vue'
import DossierDetailView from '@/features/interface/detail/dossier/DossierDetailView.vue'
import { useDossierRoutingService } from '@/features/interface/detail/dossier/useDossierRoutingService'
import FactureDetailView from '@/features/interface/detail/facture/FactureDetailView.vue'
import { useFactureRoutingService } from '@/features/interface/detail/facture/useFactureRoutingService'
import { useVirementRoutingService } from '@/features/interface/detail/virement/useVirementRoutingService'
import VirementDetailView from '@/features/interface/detail/virement/VirementDetailView.vue'
import AideContactComponent from '@/features/interface/help/AideContactComponent.vue'
import HomeView from '@/features/interface/home/HomeView.vue'
import OnboardingView from '@/features/interface/onboarding/OnboardingView.vue'
import ContactsSectionView from '@/features/interface/profil-utilisateur/sections/contacts/ContactsSection.vue'
import ProfilSectionView from '@/features/interface/profil-utilisateur/sections/profil/ProfilSection.vue'
import RattachementsView from '@/features/interface/profil-utilisateur/sections/rattachements/RattachementsView.vue'
import RechercheDemandeTtView from '@/features/interface/recherches/demande-tt/DemandeView.vue'
import RechercheDemandeView from '@/features/interface/recherches/demande/DemandeView.vue'
import RechercheDossierTtView from '@/features/interface/recherches/dossier-tt/DossierView.vue'
import RechercheDossierView from '@/features/interface/recherches/dossier/DossierView.vue'
import FactureView from '@/features/interface/recherches/facture/FactureView.vue'
import VirementView from '@/features/interface/recherches/virement/VirementView.vue'
import AccessDeniedView from '@/features/interface/shared/access-denied/AccessDeniedView.vue'

// Pour test. A supprimer en prod
import type { RechercheDemandeQueryDTO } from '@/features/api/extranet/dto/Demande/Recherche'
import type { RechercheQueryDemandeRemboursementDTO } from '@/features/api/extranet/dto/Demande/Recherche/Remboursements'
import type { RechercheDossierQueryDTO } from '@/features/api/extranet/dto/Dossier/Recherche'
import type { RechercheQueryDossierRemboursementDTO } from '@/features/api/extranet/dto/Dossier/Recherche/Remboursements'
import type { FactureQueryDTO } from '@/features/api/extranet/dto/Facture/FactureQueryDTO.ts'
import type { VirementQueryDTO } from '@/features/api/extranet/dto/Virement/VirementQueryDTO.ts'
import { registerPopupAutoGuard } from '@/features/application/popup-auto/registerPopupAutoGuard'
import { DispositifLibelleCreation } from '@/features/application/shared'
import { AuthAktoEtats } from '@/features/auth/authentification/modeles'
import type { Periode } from '@/features/domaine/types/opco/shared/Periode.ts'
import HabilitationsAdminView from '@/features/interface/profil-utilisateur/sections/admin/HabilitationsAdminView.vue'
import type { PagedRequest } from '@/features/interface/recherches/shared/composables/storeRechercheBuilder.ts'
import { isNotNullUndefinedOrEmptyString, locationQueryValueToArray, parseBool, parseIntWithDefault } from '@/features/shared/tools'
import { TabsView } from '@/features/interface/shared/tabs-view/index.ts'
import TasksView from '@/views/TasksView.vue'
import { routesPagesTests } from './index.tests'
import type {
  NavigateToAdministration,
  NavigateToContacts,
  NavigateToDemande,
  NavigateToDemandeSor,
  NavigateToDossier,
  NavigateToFactureOuVirement,
  NavigateToLocation,
  NavigateToMeta,
  NavigateToProfil,
  NavigateToQuery,
  NavigateToQueryDemandeFormation,
  NavigateToQueryDossierFormation,
  NavigateToQueryDR,
  NavigateToQueryDRF,
  NavigateToQueryFactures,
  NavigateToQueryProforma,
  NavigateToQueryVirements,
  NavigateToRattachements,
  ToModule,
  ToSousDispositifsFormationFPE,
  WithNavigateToLocation,
} from './navigateTo/'
import { registerNavigationEvents } from './navigation-events'
import './RouteMeta'

import type { RechercheQueryProformaDTO } from '@/features/api/extranet/dto/Demande/Recherche/Proforma'
import type { RechercheQueryDemandeReservationFondsDTO } from '@/features/api/extranet/dto/Demande/Recherche/ReservationFonds'
import { useAppStateStore } from '@/features/application'
import { useDossierTTRoutingService } from '@/features/interface/detail/dossier-tt/useDossierTTRoutingService.ts'
import { useDossierTTStore } from '@/features/interface/detail/dossier-tt/useDossierTTStore.ts'
import { locationToParams } from '@/features/application/locations'

const BASE_CONTEXT_URL = '/:context(adherent|prestataire)?'
const BASE_CONTEXT_ADHERENT_URL = '/:context(adherent)?'

function locationToQueryRoute(location: NavigateToQuery) {
  return {
    params: {
      context: location.context,
    } as RouteParamsGeneric,
    query: {
      ...location.query,
      orderBy: location.orderBy,
      ...location.pagination,
    } as LocationQueryRaw,
  }
}

function rechercheBeforeEnter(to: RouteLocationNormalized) {
  const location = to.meta.navigateToHelper!.parseRoute!.call(null, to)! as NavigateToQuery
  const store = locationToParams(location).useStore()
  return store.loadData(location as any)
}

async function detailBeforeEnter(to: RouteLocationNormalized) {
  const location = to.meta.navigateToHelper!.parseRoute!.call(null, to)
  const { loadStoreWithKey } = useDemandeRoutingService()
  const statut = await loadStoreWithKey(location)
  if (statut === 'Access Denied') {
    return { name: 'access-denied', query: { to: to.fullPath } }
  }
  return statut === 'Ok'
}

function metaParseToProps<T extends NavigateToLocation>(to: RouteLocationNormalized) {
  return {
    navigateToLocation: to.meta?.navigateToHelper?.parseRoute?.call(null, to),
  } as WithNavigateToLocation<T>
}

const routesRecherches: Array<RouteRecordRaw> = [
  // Recherche dossiers formation
  {
    path: `${BASE_CONTEXT_URL}/formations/dossiers`,
    name: 'recherche-dossier',
    component: RechercheDossierView,
    props: metaParseToProps,
    beforeEnter: (to, ...args) => to.meta?.navigateToHelper?.beforeEnter?.call(null, to, ...args),
    meta: {
      requiresAuth: true,
      requiresContext: true,
      toggleContextResolver: 'recherche-dossier',
      navigateToHelper: {
        isRouteValid: (location) =>
          location.to === 'recherche' && location.domaine === 'formations' && location.module === 'dossier',
        locationToRoute: locationToQueryRoute,
        parseRoute: (to) => {
          const query = to.query
          const pageRequest: PagedRequest<RechercheDossierQueryDTO> = {
            query: {
              search: locationQueryValueToArray(query.search), // à vérifier array vs string
              estSousMandatGestion: parseBool(query.estSousMandatGestion as string),
              avecSubrogation: parseBool(query.avecSubrogation as string),
              statuts: locationQueryValueToArray(query.statuts, []) as any,
              dispositifs: locationQueryValueToArray(query.dispositifs, []) as any,
              avecAlerteActionAFaire: parseBool(query.avecAlerteActionAFaire as string),
              estFormationInterne: parseBool(query.estFormationInterne as string),
              periode: query.periode as Periode,
              typePublic: (query.typePublic as TypePublic) || undefined,
              estProprietaire: parseBool(query.estProprietaire as string),
            },
            orderBy: locationQueryValueToArray(query.orderBy, [])!, // verifier
            pagination: {
              pageNumber: parseIntWithDefault(query.pageNumber as string, 1)!,
              pageSize: parseIntWithDefault(query.pageSize as string) as number | undefined,
            },
          }
          return {
            to: 'recherche',
            domaine: 'formations',
            module: 'dossier',
            ...pageRequest,
          }
        },
        beforeEnter: rechercheBeforeEnter,
      } as NavigateToMeta<NavigateToQueryDossierFormation>,
      label: 'Dossiers',
      icon: 'folder-open',
      order: 2,
      visibility: 'visible',
    },
  },
  // Recherche demandes formation
  {
    path: `${BASE_CONTEXT_URL}/formations/demandes`,
    name: 'recherche-demandes',
    component: RechercheDemandeView,
    props: metaParseToProps,
    beforeEnter: (to, ...args) => to.meta?.navigateToHelper?.beforeEnter?.call(null, to, ...args),
    meta: {
      requiresAuth: true,
      requiresContext: true,
      toggleContextResolver: 'recherche-demandes',
      label: 'Brouillons',
      navigateToHelper: {
        isRouteValid: (location) =>
          location.to === 'recherche' && location.domaine === 'formations' && location.module === 'demande',
        locationToRoute: locationToQueryRoute,
        parseRoute: (to) => {
          const query = to.query
          const pageRequest: PagedRequest<RechercheDemandeQueryDTO> = {
            query: {
              search: locationQueryValueToArray(query.search),
              estSousMandatGestion: parseBool(query.estSousMandatGestion as string),
              avecSubrogation: parseBool(query.avecSubrogation as string),
              statuts: locationQueryValueToArray(query.statuts, []) as any,
              dispositifs: locationQueryValueToArray(query.dispositifs, []) as any,
              periode: query.periode as Periode,
              typePublic: (query.typePublic as TypePublic) || undefined,
              estProprietaire: parseBool(query.estProprietaire as string),
            },
            orderBy: locationQueryValueToArray(query.orderBy, [])!, // verifier
            pagination: {
              pageNumber: parseIntWithDefault(query.pageNumber as string, 1)!,
              pageSize: parseIntWithDefault(query.pageSize as string) as number | undefined,
            },
          }
          return {
            to: 'recherche',
            domaine: 'formations',
            module: 'demande',
            ...pageRequest,
          }
        },
        beforeEnter: rechercheBeforeEnter,
      } as NavigateToMeta<NavigateToQueryDemandeFormation>,
      icon: 'edit',
      order: 3,
    },
  },
  // Recherche demandes DRF et Dossiers DRF
  {
    path: `${BASE_CONTEXT_ADHERENT_URL}/drf`,
    component: TabsView,
    name: 'DRF',
    props: {
      tabsPlacement: 'start',
      persistentTabs: true,
    },
    redirect: { name: 'recherche-drf-demandes' },
    meta: {
      requiresAuth: true,
      requiresContext: true,
      label: 'Réservations de fonds',
      icon: 'drf',
      requirePermission: 'FPE',
      visibility: () => (useAppStateStore().estFeatureVisible('FPE') ? 'visible' : 'hidden'),
      order: 4,
    },
    children: [
      {
        path: 'demandes',
        name: 'recherche-drf-demandes',
        component: () => import('@/features/interface/recherches/drf/DRFView.vue'),
        props: metaParseToProps,
        beforeEnter: (to, ...args) => to.meta?.navigateToHelper?.beforeEnter?.call(null, to, ...args),
        meta: {
          label: 'Brouillons de réservations de fonds',
          tabLabel: 'Brouillons',
          navigateToHelper: {
            isRouteValid: (location) =>
              location.to === 'recherche' && location.domaine === 'drf' && location.module === 'demande',
            locationToRoute: locationToQueryRoute,
            parseRoute: ({ query }) => {
              const pageRequest: PagedRequest<RechercheQueryDemandeReservationFondsDTO> = {
                query: {
                  search: locationQueryValueToArray(query.search),
                  statuts: locationQueryValueToArray(query.statuts, []) as any,
                  periode: query.periode as Periode,
                  typePublic: query.typePublic as TypePublic,
                  estProprietaire: parseBool(query.estProprietaire as string),
                },
                orderBy: locationQueryValueToArray(query.orderBy, [])!,
                pagination: {
                  pageNumber: parseIntWithDefault(query.pageNumber as string, 1)!,
                  pageSize: parseIntWithDefault(query.pageSize as string) as number | undefined,
                },
              }
              return {
                to: 'recherche',
                domaine: 'drf',
                module: 'demande',
                ...pageRequest,
              }
            },
            beforeEnter: rechercheBeforeEnter,
          } as NavigateToMeta<NavigateToQueryDRF>,
        },
      },
      {
        path: 'dossiers',
        name: 'recherche-drf-dossiers',
        component: () => import('@/features/interface/recherches/drf/DRFView.vue'),
        props: metaParseToProps,
        beforeEnter: (to, ...args) => to.meta?.navigateToHelper?.beforeEnter?.call(null, to, ...args),
        meta: {
          label: 'Dossiers de réservations de fonds',
          tabLabel: 'Dossiers',
          navigateToHelper: {
            isRouteValid: (location) =>
              location.to === 'recherche' && location.domaine === 'drf' && location.module === 'dossier',
            locationToRoute: locationToQueryRoute,
            parseRoute: ({ query }) => {
              const pageRequest: PagedRequest<RechercheQueryDemandeReservationFondsDTO> = {
                query: {
                  search: locationQueryValueToArray(query.search),
                  statuts: locationQueryValueToArray(query.statuts, []) as any,
                  periode: query.periode as Periode,
                  typePublic: query.typePublic as TypePublic,
                },
                orderBy: locationQueryValueToArray(query.orderBy, [])!,
                pagination: {
                  pageNumber: parseIntWithDefault(query.pageNumber as string, 1)!,
                  pageSize: parseIntWithDefault(query.pageSize as string) as number | undefined,
                },
              }
              return {
                to: 'recherche',
                domaine: 'drf',
                module: 'dossier',
                ...pageRequest,
              }
            },
            beforeEnter: rechercheBeforeEnter,
          } as NavigateToMeta<NavigateToQueryDRF>,
        },
      },
    ],
  },
  // Recherche factures
  {
    path: `${BASE_CONTEXT_URL}/factures`,
    name: 'recherche-factures',
    component: FactureView,
    props: metaParseToProps,
    beforeEnter: (to, ...args) => to.meta?.navigateToHelper?.beforeEnter?.call(null, to, ...args),
    meta: {
      requiresAuth: true,
      requiresContext: true,
      requirePermission: 'Facture',
      navigateToHelper: {
        isRouteValid: (location) => location.to === 'recherche' && location.domaine === 'factures',
        locationToRoute: locationToQueryRoute,
        parseRoute: ({ query }) => {
          const pageRequest: PagedRequest<FactureQueryDTO> = {
            query: {
              search: locationQueryValueToArray(query.search),
              avecAlerteActionAFaire: parseBool(query.avecAlerteActionAFaire as string),
              statuts: locationQueryValueToArray(query.statuts, []) as any,
              periodeDateReception: (query.periodeDateReception as Periode) ?? undefined,
              periodeDateEmission: (query.periodeDateEmission as Periode) ?? undefined
            },
            orderBy: locationQueryValueToArray(query.orderBy, [])!, // verifier
            pagination: {
              pageNumber: parseIntWithDefault(query.pageNumber as string, 1)!,
              pageSize: parseIntWithDefault(query.pageSize as string) as number | undefined,
            },
          }
          return {
            to: 'recherche',
            domaine: 'factures',
            ...pageRequest,
          }
        },
        beforeEnter: rechercheBeforeEnter,
      } as NavigateToMeta<NavigateToQueryFactures>,
      toggleContextResolver: 'recherche-factures',
      label: 'Factures',
      icon: 'bill',
      order: 7,
      visibility: 'visible',
    },
  },
  // Recherche virements
  {
    path: `${BASE_CONTEXT_URL}/virements`,
    name: 'recherche-virements',
    component: VirementView,
    props: metaParseToProps,
    beforeEnter: (to, ...args) => to.meta?.navigateToHelper?.beforeEnter?.call(null, to, ...args),
    meta: {
      requiresAuth: true,
      requiresContext: true,
      requirePermission: 'Facture',
      toggleContextResolver: 'recherche-virements',
      navigateToHelper: {
        isRouteValid: (location) => location.to === 'recherche' && location.domaine === 'virements',
        locationToRoute: locationToQueryRoute,
        parseRoute: ({ query }) => {
          const pageRequest: PagedRequest<VirementQueryDTO> = {
            query: {
              search: locationQueryValueToArray(query.search),
              periodeDateEmission: query.periodeDateEmission as Periode
            },
            orderBy: locationQueryValueToArray(query.orderBy, [])!, // verifier
            pagination: {
              pageNumber: parseIntWithDefault(query.pageNumber as string, 1)!,
              pageSize: parseIntWithDefault(query.pageSize as string) as number | undefined,
            },
          }
          return {
            to: 'recherche',
            domaine: 'virements',
            ...pageRequest,
          }
        },
        beforeEnter: rechercheBeforeEnter,
      } as NavigateToMeta<NavigateToQueryVirements>,
      label: 'Virements',
      icon: 'payment',
      order: 8,
      visibility: 'visible',
    },
  },
  // Recherche demandes DR, Dossiers DR et Proformas
  {
    path: `${BASE_CONTEXT_ADHERENT_URL}/dr`,
    component: TabsView,
    name: 'DR',
    props: {
      tabsPlacement: 'start',
      persistentTabs: true,
    },
    redirect: { name: 'recherche-dr-demandes' },
    meta: {
      requiresAuth: true,
      requiresContext: true,
      requirePermission: 'DR',
      visibility: () => (useAppStateStore().estFeatureVisible('DR' as any) ? 'visible' : 'hidden'),
      label: 'Demandes de remboursement',
      icon: 'add-square',
      order: 9,
    },
    children: [
      {
        path: 'demandes',
        name: 'recherche-dr-demandes',
        strict: true,
        component: () => import('@/features/interface/recherches/dr/DRView.vue'),
        props: metaParseToProps,
        beforeEnter: (to, ...args) =>
          to.meta?.navigateToHelper?.beforeEnter?.call(null, to, ...args),
        meta: {
          tabLabel: 'Brouillons',
          label: 'Demandes de remboursement',
          navigateToHelper: {
            isRouteValid: (location) =>
              location.to === 'recherche' && location.domaine === 'dr' && location.module === 'demande',
            locationToRoute: locationToQueryRoute,
            parseRoute: ({ query }) => {
              const pageRequest: PagedRequest<RechercheQueryDemandeRemboursementDTO> = {
                query: {
                  search: locationQueryValueToArray(query.search),
                  statuts: locationQueryValueToArray(query.statuts, []) as any,
                  dispositifs: locationQueryValueToArray(query.dispositifs, []) as any,
                  periode: query.periode as Periode,
                  typePublic: query.typePublic as TypePublic,
                  estProprietaire: parseBool(query.estProprietaire as string),
                },
                orderBy: locationQueryValueToArray(query.orderBy, [])!,
                pagination: {
                  pageNumber: parseIntWithDefault(query.pageNumber as string, 1)!,
                  pageSize: parseIntWithDefault(query.pageSize as string) as number | undefined,
                },
              }
              return {
                to: 'recherche',
                domaine: 'dr',
                module: 'demande',
                ...pageRequest,
              }
            },
            beforeEnter: rechercheBeforeEnter,
          } as NavigateToMeta<NavigateToQueryDR>,
        },
      },
      {
        path: 'dossiers',
        name: 'recherche-dr-dossiers',
        strict: true,
        component: () => import('@/features/interface/recherches/dr/DRView.vue'),
        props: metaParseToProps,
        beforeEnter: (to, ...args) => to.meta?.navigateToHelper?.beforeEnter?.call(null, to, ...args),
        meta: {
          label: 'Demandes de remboursement',
          tabLabel: 'Demandes (DR)',
          navigateToHelper: {
            isRouteValid: (location) =>
              location.to === 'recherche' && location.domaine === 'dr' && location.module === 'dossier',
            locationToRoute: locationToQueryRoute,
            parseRoute: ({ query }) => {
              const pageRequest: PagedRequest<RechercheQueryDossierRemboursementDTO> = {
                query: {
                  search: locationQueryValueToArray(query.search),
                  dispositifs: locationQueryValueToArray(query.dispositifs, []) as any,
                  statuts: locationQueryValueToArray(query.statuts, []) as any,
                  periode: query.periode as Periode,
                  typePublic: query.typePublic as TypePublic,
                },
                orderBy: locationQueryValueToArray(query.orderBy, [])!,
                pagination: {
                  pageNumber: parseIntWithDefault(query.pageNumber as string, 1)!,
                  pageSize: parseIntWithDefault(query.pageSize as string) as number | undefined,
                },
              }
              return {
                to: 'recherche',
                domaine: 'dr',
                module: 'dossier',
                ...pageRequest,
              }
            },
            beforeEnter: rechercheBeforeEnter,
          } as NavigateToMeta<NavigateToQueryDR>,
        },
      },
      {
        path: 'proforma',
        name: 'recherche-proforma',
        strict: true,
        component: () => import('@/features/interface/recherches/proforma/ProformaView.vue'),
        props: metaParseToProps,
        beforeEnter: (to, ...args) => to.meta?.navigateToHelper?.beforeEnter?.call(null, to, ...args),
        meta: {
          label: 'Demandes de remboursement',
          tabLabel: 'Proforma groupées',
          navigateToHelper: {
            isRouteValid: (location) =>
              location.to === 'recherche' && location.domaine === 'dr' && location.module === 'proforma',
            locationToRoute: locationToQueryRoute,
            parseRoute: ({ query }) => {
              const pageRequest: PagedRequest<RechercheQueryProformaDTO> = {
                query: {
                  search: locationQueryValueToArray(query.search),
                  statuts: locationQueryValueToArray(query.statuts, []) as any,
                  periode: query.periode as Periode,
                  typePublic: query.typePublic as TypePublic,
                  estProprietaire: parseBool(query.estProprietaire as string),
                },
                orderBy: locationQueryValueToArray(query.orderBy, [])!,
                pagination: {
                  pageNumber: parseIntWithDefault(query.pageNumber as string, 1)!,
                  pageSize: parseIntWithDefault(query.pageSize as string) as number | undefined,
                },
              }
              return {
                to: 'recherche',
                domaine: 'dr',
                module: 'proforma',
                ...pageRequest,
              }
            },
            beforeEnter: rechercheBeforeEnter,
          } as NavigateToMeta<NavigateToQueryProforma>,
        },
      },
    ],
  },
  // Recherche outils FPE
  {
    path: `${BASE_CONTEXT_ADHERENT_URL}/tt`,
    component: TabsView,
    name: 'TT',
    props: {
      tabsPlacement: 'start',
      persistentTabs: true,
    },
    redirect: { name: 'recherche-outils-fpe-demandes' },
    meta: {
      requiresAuth: true,
      requiresContext: true,
      requirePermission: 'FPE',
      visibility: () => (useAppStateStore().estFeatureVisible('FPE') ? 'visible' : 'hidden'),
      label: 'Badges et cartes',
      icon: 'validate',
      order: 4,
    },
    children: [
      {
        path: 'demandes',
        name: 'recherche-outils-fpe-demandes',
        component: RechercheDemandeTtView,
        props: metaParseToProps,
        beforeEnter: (to, ...args) => to.meta?.navigateToHelper?.beforeEnter?.call(null, to, ...args),
        meta: {
          tabLabel: 'Brouillons',
          navigateToHelper: {
            isRouteValid: (location) =>
              location.to === 'recherche' && location.domaine === 'tt' && location.module === 'demande',
            locationToRoute: locationToQueryRoute,
            parseRoute: (to) => {
              const query = to.query
              const pageRequest: PagedRequest<RechercheDemandeQueryDTO> = {
                query: {
                  search: locationQueryValueToArray(query.search),
                  estSousMandatGestion: parseBool(query.estSousMandatGestion as string),
                  avecSubrogation: parseBool(query.avecSubrogation as string),
                  statuts: locationQueryValueToArray(query.statuts, []) as any,
                  dispositifs: locationQueryValueToArray(query.dispositifs, []) as any,
                  periode: query.periode as Periode,
                  estProprietaire: parseBool(query.estProprietaire as string),
                },
                orderBy: locationQueryValueToArray(query.orderBy, [])!, // verifier
                pagination: {
                  pageNumber: parseIntWithDefault(query.pageNumber as string, 1)!,
                  pageSize: parseIntWithDefault(query.pageSize as string) as number | undefined,
                },
              }
              return {
                to: 'recherche',
                domaine: 'tt',
                module: 'demande',
                ...pageRequest,
              }
            },
            beforeEnter: rechercheBeforeEnter,
          } as NavigateToMeta<NavigateToQueryDemandeFormation>,
        },
      },
      {
        path: 'dossiers',
        name: 'recherche-outils-fpe-dossiers',
        component: RechercheDossierTtView,
        props: metaParseToProps,
        beforeEnter: (to, ...args) => to.meta?.navigateToHelper?.beforeEnter?.call(null, to, ...args),
        meta: {
          tabLabel: 'Dossiers',
          navigateToHelper: {
            isRouteValid: (location) =>
              location.to === 'recherche' && location.domaine === 'tt' && location.module === 'dossier',
            locationToRoute: locationToQueryRoute,
            parseRoute: (to) => {
              const query = to.query
              const pageRequest: PagedRequest<RechercheDossierQueryDTO> = {
                query: {
                  search: locationQueryValueToArray(query.search), // à vérifier array vs string
                  estSousMandatGestion: parseBool(query.estSousMandatGestion as string),
                  avecSubrogation: parseBool(query.avecSubrogation as string),
                  statuts: locationQueryValueToArray(query.statuts, []) as any,
                  dispositifs: locationQueryValueToArray(query.dispositifs, []) as any,
                  avecAlerteActionAFaire: parseBool(query.avecAlerteActionAFaire as string),
                  estFormationInterne: parseBool(query.estFormationInterne as string),
                  periode: query.periode as Periode,
                  estProprietaire: parseBool(query.estProprietaire as string),
                },
                orderBy: locationQueryValueToArray(query.orderBy, [])!, // verifier
                pagination: {
                  pageNumber: parseIntWithDefault(query.pageNumber as string, 1)!,
                  pageSize: parseIntWithDefault(query.pageSize as string) as number | undefined,
                },
              }
              return {
                to: 'recherche',
                domaine: 'tt',
                module: 'dossier',
                ...pageRequest,
              }
            },
            beforeEnter: rechercheBeforeEnter,
          } as NavigateToMeta<NavigateToQueryDossierFormation>,
        },
      },
    ],
  },
]

const routeDetails: Array<RouteRecordRaw> = [
  // Détail dossiers formation
  {
    path: `${BASE_CONTEXT_URL}/formations/dossiers/:code`,
    name: 'dossier-detail',
    component: DossierDetailView,
    beforeEnter: async (to) => {
      const code = to.meta.navigateToHelper!.parseRoute!.call(null, to).code
      const { loadStoreWithKey } = useDossierRoutingService()
      return await loadStoreWithKey(code)
    },
    props: metaParseToProps,
    meta: {
      requiresAuth: true,
      requiresContext: true,
      navigateToHelper: {
        isRouteValid: (location) =>
          location.to === 'detail' && location.domaine === 'formations' && location.module === 'dossier',
        locationToRoute: (location) => ({
          params: {
            context: location.context,
            code: location.domaine === 'formations' ? location.code : undefined,
          },
        }),
        parseRoute: (to) => {
          return {
            to: 'detail',
            domaine: 'formations',
            module: 'dossier',
            context: (to.params.context as string).toLowerCase(),
            code: to.params.code,
          }
        },
      } as NavigateToMeta<NavigateToDossier>,
    },
  },
  // Détail dossiers outil FPE
  {
    path: `${BASE_CONTEXT_ADHERENT_URL}/tt/dossiers/:code`,
    name: 'dossier-outils-fpe-detail',
    component: DossierOutilFpeDetailView,
    beforeEnter: async (to) => {
      const code = to.meta.navigateToHelper!.parseRoute!.call(null, to).code
      const { loadStoreWithKey } = useDossierTTRoutingService()
      return await loadStoreWithKey(code)
    },
    props: metaParseToProps,
    meta: {
      requiresAuth: true,
      requiresContext: true,
      label: () => {
        const store = useDossierTTStore()
        return [`Dossier badges et cartes`, SousDispositifTTLibelle[store.model!.sousDispositif as SousDispositifTT]]
      },
      navigateToHelper: {
        isRouteValid: (location) =>
          location.to === 'detail' && location.domaine === 'tt' && location.module === 'dossier',
        locationToRoute: (location) => ({
          params: {
            context: location.context,
            code: location.domaine === 'tt' ? location.code : undefined,
          },
        }),
        parseRoute: (to) => {
          return {
            to: 'detail',
            domaine: 'tt',
            module: 'dossier',
            context: 'adherent',
            code: to.params.code,
          }
        },
      } as NavigateToMeta<NavigateToDossier>,
    },
  },
  // Détail demandes formation
  {
    path: `${BASE_CONTEXT_URL}/:domaine(formations|drf|tt)/demandes/:dispositif/:sousDispositif/:id`,
    name: 'demande-detail',
    component: demande,
    beforeEnter: detailBeforeEnter,
    props: metaParseToProps,
    meta: {
      requiresAuth: true,
      requiresContext: true,
      label: (to) => {
        // Todo : à revoir
        const location = to.meta.navigateToHelper!.parseRoute!.call(null, to) as NavigateToDemande
        const store = locationToParams(location).setup.useStore()

        const dispositif = store.model!.dispositif as Dispositif
        const libelleDemande = DispositifLibelleCreation[dispositif]
        if ('sousDispositif' in store.model! && store.model.sousDispositif) {
          const sousDispositifs = DispositifSousDipositif[dispositif]
          if (!sousDispositifs) return libelleDemande
          const key = store.model.sousDispositif as keyof typeof sousDispositifs
          if (store.model?.typeDemande === 'ReservationFonds') {
            return ['Nouvelle demande de réservation de fonds ' + store.model?.sousDispositif, sousDispositifs[key]]
          } else {
            return [libelleDemande, sousDispositifs[key]]
          }
        } else {
          return libelleDemande
        }
      },
      navigateToHelper: {
        isRouteValid: (location) =>
          location.to === 'detail' &&
          ['formations', 'drf', 'tt'].includes(location.domaine) &&
          'module' in location &&
          location.module === 'demande' &&
          'dispositif' in location &&
          location.dispositif !== 'capp',
        locationToRoute: (location) => ({
          params: {
            context: location.context,
            domaine: location.domaine,
            dispositif: 'dispositif' in location ? location.dispositif : undefined,
            sousDispositif: 'sousDispositif' in location ? location.sousDispositif.toLowerCase() : undefined,
            id: 'id' in location ? `${location.id}` : '',
          },
        }),
        parseRoute: (to) => {
          return {
            to: 'detail',
            context: (to.params['context'] as string).toLowerCase() as any,
            domaine: (to.params['domaine'] as string).toLowerCase() as 'formations' | 'drf' | 'tt',
            module: 'demande',
            dispositif: (to.params['dispositif'] as string).toLowerCase() as any,
            sousDispositif: to.params['sousDispositif'],
            id: parseInt(to.params['id'] as string),
          } as NavigateToDemande
        },
      } as NavigateToMeta<NavigateToDemande>,
    },
  },
  // Détail demandes formation CAPP
  {
    path: `${BASE_CONTEXT_URL}/formations/demandes/capp/:id`,
    name: 'demande-capp-detail',
    component: demande,
    beforeEnter: detailBeforeEnter,
    props: metaParseToProps,
    meta: {
      requiresAuth: true,
      requiresContext: true,
      label: DispositifLibelleCreation['CAPP'],
      navigateToHelper: {
        isRouteValid: (location) =>
          location.to === 'detail' &&
          location.domaine === 'formations' &&
          location.module === 'demande' &&
          location.dispositif === 'capp',
        locationToRoute: (location) => ({
          params: {
            context: location.context,
            id: 'id' in location ? `${location.id}` : '',
          },
        }),
        parseRoute: (to) => {
          const props: NavigateToDemande = {
            to: 'detail',
            domaine: 'formations',
            dispositif: 'capp',
            module: 'demande',
            context: (to.params['context'] as string).toLowerCase() as any,
            id: parseInt(to.params['id'] as string),
          }
          return props
        },
      } as NavigateToMeta<NavigateToDemande>,
    },
  },
  // Détail dossiers DRF CXPI
  {
    path: `${BASE_CONTEXT_ADHERENT_URL}/drf/dossiers/cxpi/:sousDispositif/:code`,
    name: 'dossier-drf-detail',
    component: demande,
    beforeEnter: detailBeforeEnter,
    props: metaParseToProps,
    meta: {
      requiresAuth: true,
      requiresContext: true,
      label: (to) => {
        const sousDispositif = (to.params['sousDispositif'] as string).toUpperCase() as 'CIPI' | 'CDPI'
        const code = to.params['code'] as string
        return [`Dossier réservation de fonds ${sousDispositif}`, code]
      },
      requirePermission: 'FPE',
      navigateToHelper: {
        isRouteValid: (location) =>
          location.to === 'detail' &&
          location.domaine === 'drf' &&
          location.module === 'dossier' &&
          location.dispositif === 'fpe' &&
          ['cipi', 'cdpi'].includes(location.sousDispositif),
        locationToRoute: (location) => ({
          params: {
            context: 'adherent',
            sousDispositif: location.domaine === 'drf' ? location.sousDispositif : undefined,
            code: location.domaine === 'drf' ? `${location.code}` : undefined,
          },
        }),
        parseRoute: (to) => {
          const props: NavigateToDemandeSor = {
            to: 'detail',
            domaine: 'drf',
            module: 'dossier',
            dispositif: 'fpe',
            sousDispositif: (to.params['sousDispositif'] as string).toLowerCase() as Extract<
              ToSousDispositifsFormationFPE,
              'cdpi' | 'cipi'
            >,
            context: 'adherent',
            code: to.params['code'] as string,
          }
          return props
        },
      } as NavigateToMeta<NavigateToDemandeSor>,
    },
  },
  // Détail demandes DR
  {
    path: `${BASE_CONTEXT_ADHERENT_URL}/dr/:module(demandes|dossiers|proforma)/:id`,
    name: 'dr-detail',
    component: demande,
    beforeEnter: detailBeforeEnter,
    props: metaParseToProps,
    meta: {
      requiresAuth: true,
      requiresContext: true,
      label: (to) => {
        const location = to.meta.navigateToHelper!.parseRoute!.call(null, to) as NavigateToDemande

        const store = locationToParams(location).setup.useStore()
        const dispositif = store.model!.dispositif as DispositifDR
        const libelleDispositif = DispositifDRLibelle[dispositif]

        const labels: Record<ToModule, string> = {
          demande: 'Nouvelle demande de remboursement',
          dossier: 'Demande de remboursement',
          proforma: 'Nouvelle facture proforma groupée',
        }
        const toModule = to.meta.navigateToHelper!.parseRoute!.call(null, to).module as ToModule

        if (location.domaine === 'dr') {
          const titre = libelleDispositif ? `${labels[toModule]} ${libelleDispositif}` : labels[toModule]
          if ('sousDispositif' in store.model! && store.model.sousDispositif) {
            const sousDispositif = store.model.sousDispositif as SousDispositifDR
            if (SousDispositifDRLibelle[sousDispositif]) {
              return [titre, SousDispositifDRLibelle[sousDispositif]]
            }
          }
          return titre
        }
        return labels[toModule]
      },
      navigateToHelper: {
        isRouteValid: (location) => location.to === 'detail' && location.domaine === 'dr',
        locationToRoute: (location) => ({
          params: {
            context: 'adherent',
            module:
              'module' in location ? (location.module === 'proforma' ? 'proforma' : `${location.module}s`) : undefined,
            id: 'id' in location ? `${location.id}` : location.code,
          },
        }),
        parseRoute: (to) => {
          const moduleUrl = to.params['module']
          if (moduleUrl === 'dossiers') {
            return {
              to: 'detail',
              domaine: 'dr',
              module: 'dossier',
              context: 'adherent',
              code: to.params['id'] as string,
            } as NavigateToDemande
          } else {
            return {
              to: 'detail',
              domaine: 'dr',
              module: moduleUrl === 'demandes' ? 'demande' : 'proforma',
              context: 'adherent',
              id: parseInt(to.params['id'] as string),
            } as NavigateToDemande
          }
        },
      } as NavigateToMeta<NavigateToDemande>,
    },
  },
  // Détail factures
  {
    path: `${BASE_CONTEXT_URL}/factures/:code`,
    name: 'facture-detail',
    component: FactureDetailView,
    beforeEnter: async (to) => {
      const { loadStoreWithKey } = useFactureRoutingService()
      return await loadStoreWithKey((to.params as any).code)
    },
    props: metaParseToProps,
    meta: {
      requiresAuth: true,
      requiresContext: true,
      label: 'Détail de la facture',
      navigateToHelper: {
        isRouteValid: (location) => location.to === 'detail' && location.domaine === 'factures',
        locationToRoute: (location) => ({
          params: {
            context: location.context,
            code: location.code,
          },
        }),
        parseRoute: (to) => {
          return {
            to: 'detail',
            domaine: 'factures',
            context: (to.params.context as string).toLowerCase(),
            code: to.params.code,
          }
        },
      } as NavigateToMeta<NavigateToFactureOuVirement>,
    },
  },
  // Détail demandes virement
  {
    path: `${BASE_CONTEXT_URL}/virements/:code`,
    name: 'virement-detail',
    component: VirementDetailView,
    beforeEnter: async (to) => {
      const { loadStoreWithKey } = useVirementRoutingService()
      return await loadStoreWithKey((to.params as any).code)
    },
    props: metaParseToProps,
    meta: {
      requiresAuth: true,
      requiresContext: true,
      label: 'Détail du virement',
      navigateToHelper: {
        isRouteValid: (location) => location.to === 'detail' && location.domaine === 'virements',
        locationToRoute: (location) => ({
          params: {
            context: location.context,
            code: location.code,
          },
        }),
        parseRoute: (to) => {
          return {
            to: 'detail',
            domaine: 'virements',
            context: (to.params.context as string).toLowerCase(),
            code: to.params.code,
          }
        },
      } as NavigateToMeta<NavigateToFactureOuVirement>,
    },
  },
]

export const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'home',
    component: async () => {
      const appState = useAuthentificationStore()
      const estUtilisateurChargé = appState.etat === AuthAktoEtats.Authentifié
      return estUtilisateurChargé ? HomeView : OnboardingView
    },
    meta: {
      label: 'Accueil',
      icon: 'home',
      order: 1,
      visibility: 'visible',
    },
  },
  ...routeDetails,
  ...routesRecherches,
  {
    path: `${BASE_CONTEXT_URL}/utilisateur`,
    component: TabsView,
    name: 'Menu utilisateur',
    redirect: { name: 'Profil' },
    meta: {
      requiresAuth: true,
      toggleContextResolver: 'Menu utilisateur',
    },
    children: [
      {
        path: 'profil',
        name: 'Profil',
        component: ProfilSectionView,
        meta: {
          requiresAuth: true,
          requiresContext: true,
          toggleContextResolver: 'Profil',
          label: 'Mon profil',
          navigateToHelper: {
            isRouteValid: (location) =>
              location.to === 'utilisateur' && location.module === 'profil',
            locationToRoute: (location) =>
              ({ params: { context: location.context } }),
          } as NavigateToMeta<NavigateToProfil>,
        },
      },
      {
        path: 'rattachements',
        name: 'Rattachements',
        component: RattachementsView,
        meta: {
          requiresAuth: true,
          requiresContext: true,
          toggleContextResolver: 'Rattachements',
          navigateToHelper: {
            isRouteValid: (location) =>
              location.to === 'utilisateur' && location.module === 'rattachements',
            locationToRoute: (location) =>
              ({ params: { context: location.context } }),
          } as NavigateToMeta<NavigateToRattachements>,
        },
      },
      {
        path: 'administration/:mode?/:identifiant?',
        name: 'Administration',
        component: HabilitationsAdminView,
        props: metaParseToProps,
        meta: {
          requiresAuth: true,
          requiresContext: true,
          requiresAdmin: true,
          toggleContextResolver: (route, _context, contextApp) => {
            const params = {
              ...route.params,
              context: contextApp,
            }
            return { ...route, params }
          },
          label: 'Administration des rattachements',
          navigateToHelper: {
            isRouteValid: (location) =>
              location.to === 'utilisateur' &&
              location.module === 'administration' &&
              isNotNullUndefinedOrEmptyString(location.mode),
            locationToRoute: (location) => {
              const params: RouteParamsGeneric = { context: location.context!, mode: location.mode }
              if (location.mode !== 'habilitations' && location.identifiant) params.identifiant = location.identifiant
              return { params }
            },
            parseRoute: (to) => {
              return {
                to: 'utilisateur',
                module: 'administration',
                context: (to.params['context'] as string).toLowerCase(),
                mode: to.params['mode'] || 'entite',
                identifiant: to.params['identifiant'] || undefined,
              } as NavigateToAdministration
            },
          } as NavigateToMeta<NavigateToAdministration>,
        },
      },
      {
        path: 'contacts',
        name: 'Contacts',
        component: ContactsSectionView,
        meta: {
          requiresAuth: true,
          requiresContext: true,
          toggleContextResolver: 'Contacts',
          label: 'Mes contacts',
          navigateToHelper: {
            isRouteValid: (location) =>
              location.to === 'utilisateur' && location.module === 'contacts',
            locationToRoute: (location) =>
              ({ params: { context: location.context } }),
          } as NavigateToMeta<NavigateToContacts>,
        },
      },
    ],
  },
  {
    path: `${BASE_CONTEXT_URL}/taches`,
    name: 'tasks',
    component: TasksView,
    meta: {
      requiresAuth: true,
      requiresContext: true,
      toggleContextResolver: 'tasks',
      label: 'Liste des tâches',
      icon: 'task-square',
      order: 6,
      visibility: 'visible',
    },
  },
  {
    path: '/aide',
    name: 'help',
    component: AideContactComponent,
    meta: {
      requiresAuth: true,
      label: 'Aide & Contact',
      icon: 'device-message',
      order: 20,
      visibility: 'visible',
    },
  },
  {
    path: '/access-denied',
    name: 'access-denied',
    component: AccessDeniedView,
    meta: {
      requiresAuth: true,
    },
  },
]

// Attention, route par défaut, toujours positionnée en dernier
const NOT_FOUND = {
  path: '/:pathMatch(.*)*',
  name: 'NotFound',
  redirect: { name: 'home' },
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [...routes, ...routesPagesTests, ...authRoutesAkto, ...impersoRoutes, ...contextAppRoutes, NOT_FOUND],
})

registerAuthGuard(router)
registerContextGuard(router)
registerPermissionsGuard(router)
registerPopupAutoGuard(router)
registerNavigationEvents(router)

export default router
